import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";
import {
  ArrowRight,
  CheckCircle,
  XCircle,
  CurrencyEur,
  ClipboardText,
  ChartBarHorizontal,
  ArrowsClockwise,
  Scales,
  Briefcase,
  ListNumbers,
} from "@phosphor-icons/react";

const Eyebrow = ({ children, accent = false, light = false }) => (
  <span className={`gm-eyebrow ${accent ? "text-[#0A4DFF]" : light ? "text-white/50" : ""}`}>{children}</span>
);

const Hero = () => {
  const { t } = useLang();
  const s = t.shelfmasters.hero;
  return (
    <section className="relative bg-white border-b border-[#e5e5e5]" data-testid="sm-hero">
      <div className="absolute inset-0 gm-grid-bg pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-16 md:pb-20 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-9">
            <Eyebrow accent>{s.eyebrow}</Eyebrow>
            <h1 className="font-display text-5xl md:text-7xl tracking-tighter font-medium leading-[0.95] mt-6 max-w-4xl">
              {s.title}
            </h1>
            <p className="text-lg md:text-xl text-[#525252] mt-8 max-w-3xl leading-relaxed">{s.sub}</p>
            <p className="text-base text-[#0a0a0a] mt-6 max-w-3xl leading-relaxed border-l-2 border-[#0A4DFF] pl-5">
              {s.leadIn}
            </p>
          </div>
          <div className="lg:col-span-3 flex lg:items-end">
            <div className="bg-[#0a0a0a] text-white p-6 w-full">
              <div className="font-mono text-[10px] tracking-widest text-white/50">// HEADLINE METRIC</div>
              <div className="font-display text-5xl md:text-6xl tracking-tighter mt-3 leading-none">−66%</div>
              <div className="text-sm text-white/70 mt-3 leading-relaxed">úspora ročních nákladů oproti FTE modelu při stejném pokrytí.</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Benefits = () => {
  const { t } = useLang();
  const s = t.shelfmasters.benefits;
  return (
    <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="sm-benefits">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <Eyebrow>{s.eyebrow}</Eyebrow>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">{s.title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {s.items.map((it, i) => (
            <div key={i} className="bg-white border border-[#e5e5e5] p-7 hover:border-[#0a0a0a] transition-colors">
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs tracking-widest text-[#525252]">{it.kicker}</span>
                <span className="w-2 h-2 bg-[#0A4DFF]" />
              </div>
              <h3 className="font-display text-xl md:text-2xl tracking-tight mt-8 leading-[1.1]">{it.h}</h3>
              <p className="text-sm text-[#525252] mt-3 leading-relaxed">{it.t}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Split = () => {
  const { t } = useLang();
  const s = t.shelfmasters.split;
  return (
    <section className="py-14 md:py-20 bg-white border-y border-[#e5e5e5]" data-testid="sm-split">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <Eyebrow>{s.eyebrow}</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">{s.title}</h2>
          </div>
          <p className="md:col-span-5 text-[#525252] leading-relaxed">{s.intro}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
          <div className="bg-[#0A4DFF] text-white p-8 md:p-10">
            <div className="flex items-center gap-3">
              <Briefcase size={22} weight="thin" />
              <span className="font-mono text-[10px] tracking-widest uppercase">Shelfmasters</span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl mt-5 leading-tight">{s.gm.h}</h3>
            <ul className="mt-7 space-y-3">
              {s.gm.items.map((it, i) => (
                <li key={i} className="flex items-start gap-3 text-[15px] leading-relaxed border-t border-white/15 pt-3 first:border-t-0 first:pt-0">
                  <CheckCircle size={18} weight="thin" className="shrink-0 mt-0.5" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white border border-[#e5e5e5] p-8 md:p-10">
            <div className="flex items-center gap-3 text-[#525252]">
              <ClipboardText size={22} weight="thin" />
              <span className="font-mono text-[10px] tracking-widest uppercase">Klient / Client</span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl mt-5 leading-tight">{s.client.h}</h3>
            <ul className="mt-7 space-y-3">
              {s.client.items.map((it, i) => (
                <li key={i} className="flex items-start gap-3 text-[15px] leading-relaxed border-t border-[#e5e5e5] pt-3 first:border-t-0 first:pt-0">
                  <span className="font-mono text-xs text-[#0A4DFF] mt-1 shrink-0">{`0${i + 1}`}</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

const Ops = () => {
  const { t } = useLang();
  const s = t.shelfmasters.ops;
  return (
    <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="sm-ops">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <Eyebrow>{s.eyebrow}</Eyebrow>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">{s.title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          {s.groups.map((g, i) => (
            <div key={i} className="bg-white border border-[#e5e5e5] p-7">
              <span className="font-mono text-[10px] tracking-widest text-[#525252]">{`G.0${i + 1}`}</span>
              <h3 className="font-display text-xl md:text-2xl tracking-tight mt-4">{g.h}</h3>
              <ul className="mt-5 border-t border-[#0a0a0a] divide-y divide-[#e5e5e5]">
                {g.items.map((it, j) => (
                  <li key={j} className="py-3 flex items-start gap-3 text-sm">
                    <ArrowRight size={14} weight="thin" className="text-[#0A4DFF] mt-1 shrink-0" />
                    <span className="leading-relaxed">{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Phases = () => {
  const { t } = useLang();
  const s = t.shelfmasters.phases;
  return (
    <section className="py-14 md:py-20 bg-[#0a0a0a] text-white" data-testid="sm-phases">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3">
              <ListNumbers size={22} weight="thin" className="text-[#0A4DFF]" />
              <span className="font-mono uppercase tracking-[0.22em] text-[13px] font-medium text-[#5B82FF]">{s.eyebrow}</span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">{s.title}</h2>
          </div>
          <p className="md:col-span-5 text-white/70 leading-relaxed">{s.intro}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 mt-12 border border-white/10">
          {s.items.map((p, i) => (
            <div key={i} className="p-7 md:p-9 border-b md:border-b-0 last:border-b-0 border-white/10 md:border-r last:md:border-r-0">
              <div className="flex items-baseline gap-3">
                <span className="font-display text-5xl md:text-6xl tracking-tighter text-white/40">{p.n}</span>
                <span className="font-mono text-[10px] tracking-widest text-[#0A4DFF]">{p.d}</span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl mt-3 leading-tight">{p.h}</h3>
              <ul className="mt-5 space-y-2 text-sm text-white/70">
                {p.list.map((it, j) => (
                  <li key={j} className="flex items-start gap-2 leading-relaxed">
                    <span className="text-[#0A4DFF] mt-0.5">·</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Models = () => {
  const { t } = useLang();
  const s = t.shelfmasters.models;
  return (
    <section className="py-14 md:py-20 bg-white border-b border-[#e5e5e5]" data-testid="sm-models">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3">
            <Scales size={22} weight="thin" className="text-[#0A4DFF]" />
            <Eyebrow accent>{s.eyebrow}</Eyebrow>
          </div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">{s.title}</h2>
          <p className="text-[#525252] mt-5 leading-relaxed">{s.intro}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-12">
          {/* FTE */}
          <div className="bg-[#fafafa] border border-[#e5e5e5] p-8 md:p-10">
            <div className="flex items-center justify-between">
              <span className="gm-eyebrow text-[#FF3B30]">{s.fte.tone}</span>
              <XCircle size={22} weight="thin" className="text-[#FF3B30]" />
            </div>
            <h3 className="font-display text-2xl md:text-3xl mt-4 leading-tight">{s.fte.h}</h3>
            <table className="w-full mt-6 text-sm">
              <tbody>
                {s.fte.rows.map((r, i) => (
                  <tr key={i} className="border-t border-[#e5e5e5]">
                    <td className="py-3 pr-4 text-[#525252] font-mono text-xs uppercase tracking-wider align-top w-2/5">{r[0]}</td>
                    <td className="py-3 text-[#0a0a0a] leading-relaxed">{r[1]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* SLA */}
          <div className="bg-[#0a0a0a] text-white p-8 md:p-10 relative">
            <div className="flex items-center justify-between">
              <span className="gm-eyebrow text-[#0A4DFF]">{s.sla.tone}</span>
              <CheckCircle size={22} weight="thin" className="text-[#0A4DFF]" />
            </div>
            <h3 className="font-display text-2xl md:text-3xl mt-4 leading-tight">{s.sla.h}</h3>
            <table className="w-full mt-6 text-sm">
              <tbody>
                {s.sla.rows.map((r, i) => (
                  <tr key={i} className="border-t border-white/15">
                    <td className="py-3 pr-4 text-white/50 font-mono text-xs uppercase tracking-wider align-top w-2/5">{r[0]}</td>
                    <td className="py-3 leading-relaxed">{r[1]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-base text-[#0a0a0a] mt-8 max-w-4xl leading-relaxed border-l-2 border-[#0A4DFF] pl-5 italic">
          {s.slaDef}
        </p>
      </div>
    </section>
  );
};

const Kpis = () => {
  const { t } = useLang();
  const s = t.shelfmasters.kpis;
  return (
    <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="sm-kpis">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3">
            <ChartBarHorizontal size={22} weight="thin" />
            <Eyebrow>{s.eyebrow}</Eyebrow>
          </div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">{s.title}</h2>
        </div>
        <div className="mt-10 overflow-x-auto bg-white border border-[#e5e5e5]">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {s.cols.map((c, i) => (
                  <th key={i} className="text-left font-mono text-[10px] tracking-widest text-[#525252] uppercase border-b-2 border-[#0a0a0a] px-5 py-4">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {s.rows.map((r, i) => (
                <tr key={i} className="hover:bg-[#fafafa] transition-colors">
                  {r.map((cell, j) => (
                    <td key={j} className={`px-5 py-4 border-b border-[#e5e5e5] ${j === 0 ? "font-mono text-[#0A4DFF]" : "text-[#0a0a0a]"} ${j === 2 ? "font-display tracking-tight" : ""}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

const Reversibility = () => {
  const { t } = useLang();
  const s = t.shelfmasters.reversibility;
  return (
    <section className="py-14 md:py-20 bg-white border-y border-[#e5e5e5]" data-testid="sm-reversibility">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3">
              <ArrowsClockwise size={22} weight="thin" className="text-[#0A4DFF]" />
              <Eyebrow accent>{s.eyebrow}</Eyebrow>
            </div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">{s.title}</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {s.items.map((it, i) => (
            <div key={i} className="border-t-2 border-[#0a0a0a] pt-5">
              <span className="font-mono text-[10px] tracking-widest text-[#525252]">{`R.0${i + 1}`}</span>
              <h3 className="font-display text-xl md:text-2xl mt-3 tracking-tight leading-tight">{it.h}</h3>
              <p className="text-sm text-[#525252] mt-3 leading-relaxed">{it.t}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Economics = () => {
  const { t } = useLang();
  const s = t.shelfmasters.economics;
  return (
    <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="sm-economics">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3">
            <CurrencyEur size={22} weight="thin" className="text-[#0A4DFF]" />
            <Eyebrow accent>{s.eyebrow}</Eyebrow>
          </div>
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-5 leading-[0.95] font-medium">{s.title}</h2>
          <p className="text-[#525252] mt-5 leading-relaxed">{s.intro}</p>
        </div>

        {/* FTE cost breakdown + cost per visit */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mt-10">
          <div className="md:col-span-7 bg-white border border-[#e5e5e5] p-7 md:p-9">
            <h3 className="font-display text-xl md:text-2xl">{s.fteCost.h}</h3>
            <table className="w-full mt-5 text-sm">
              <tbody>
                {s.fteCost.items.map((r, i) => (
                  <tr key={i} className="border-t border-[#e5e5e5]">
                    <td className="py-3 pr-4 text-[#525252] leading-relaxed">{r[0]}</td>
                    <td className="py-3 text-right font-mono text-[#0a0a0a]">{r[1]}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-[#0a0a0a]">
                  <td className="py-4 font-mono text-xs uppercase tracking-widest text-[#525252]">Total</td>
                  <td className="py-4 text-right font-display text-2xl md:text-3xl tracking-tight text-[#0A4DFF]">{s.fteCost.total}</td>
                </tr>
              </tbody>
            </table>
            <p className="text-xs text-[#9a9a9a] mt-5 leading-relaxed italic">{s.fteCost.footnote}</p>
          </div>

          <div className="md:col-span-5 bg-[#0a0a0a] text-white p-7 md:p-9 flex flex-col">
            <h3 className="font-display text-xl md:text-2xl">{s.compare.h}</h3>
            <div className="grid grid-cols-2 gap-4 mt-7">
              <div className="border-t-2 border-white/30 pt-4">
                <span className="gm-eyebrow text-white/50">{s.compare.fte.label}</span>
                <div className="font-display text-3xl md:text-4xl tracking-tight mt-2">{s.compare.fte.value}</div>
                <div className="text-[10px] font-mono tracking-widest text-white/40 mt-2 uppercase">{s.compare.fte.sub}</div>
              </div>
              <div className="border-t-2 border-[#0A4DFF] pt-4">
                <span className="gm-eyebrow text-[#0A4DFF]">{s.compare.sla.label}</span>
                <div className="font-display text-3xl md:text-4xl tracking-tight mt-2 text-[#0A4DFF]">{s.compare.sla.value}</div>
                <div className="text-[10px] font-mono tracking-widest text-white/40 mt-2 uppercase">{s.compare.sla.sub}</div>
              </div>
            </div>
            <div className="mt-auto pt-7">
              <div className="font-display text-6xl md:text-7xl tracking-tighter leading-none">{s.compare.delta}</div>
              <div className="text-xs font-mono tracking-widest text-white/50 uppercase mt-3">{s.compare.deltaLabel}</div>
            </div>
          </div>
        </div>

        {/* Big number: 2000 visits/month */}
        <div className="mt-12 bg-[#0A4DFF] text-white p-8 md:p-12">
          <Eyebrow light>// CASE</Eyebrow>
          <div className="font-display text-3xl md:text-5xl tracking-tight mt-3 leading-tight">{s.big.eyebrow}</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-10 border-t border-white/20 pt-8">
            <div>
              <div className="font-mono text-[10px] tracking-widest text-white/60 uppercase">{s.big.fte.label}</div>
              <div className="font-display text-2xl md:text-3xl tracking-tight mt-2 line-through opacity-70">{s.big.fte.value}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-widest text-white/60 uppercase">{s.big.sla.label}</div>
              <div className="font-display text-2xl md:text-3xl tracking-tight mt-2">{s.big.sla.value}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-widest text-white/60 uppercase">{s.big.monthly.label}</div>
              <div className="font-display text-2xl md:text-3xl tracking-tight mt-2">{s.big.monthly.value}</div>
            </div>
            <div className="bg-white/10 -m-4 p-4">
              <div className="font-mono text-[10px] tracking-widest text-white/80 uppercase">{s.big.yearly.label}</div>
              <div className="font-display text-3xl md:text-5xl tracking-tighter mt-2 leading-none">{s.big.yearly.value}</div>
            </div>
          </div>
          <p className="text-base md:text-lg mt-8 max-w-3xl leading-relaxed text-white/90">{s.big.claim}</p>
        </div>
      </div>
    </section>
  );
};

const Cta = () => {
  const { t } = useLang();
  const s = t.shelfmasters.cta;
  return (
    <section className="py-14 md:py-20 bg-white border-t border-[#e5e5e5]" data-testid="sm-cta">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-4xl">
          <Eyebrow accent>{s.eyebrow}</Eyebrow>
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-5 leading-[0.95] font-medium">{s.title}</h2>
          <p className="text-[#525252] mt-5 leading-relaxed max-w-2xl">{s.sub}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="mailto:info@globalmarketing.cz" className="gm-btn gm-btn-primary" data-testid="sm-cta-email">
              info@globalmarketing.cz <ArrowRight size={16} />
            </a>
            <Link to="/kontakt" className="gm-btn gm-btn-outline" data-testid="sm-cta-form">
              {t.form.title} →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Shelfmasters() {
  return (
    <div data-testid="shelfmasters-page">
      <Seo
        title={{ cs: "Shelfmasters · Outsourcing retailové exekuce", en: "Shelfmasters · Outsourced retail execution" }}
        description={{
          cs:
            "SLA model místo FTE — −66 % nákladů, měřitelné KPI, žádný lock-in. Kompletní outsourcing retailové exekuce v CZ a SK.",
          en:
            "SLA over FTE — −66% cost, measurable KPIs, no lock-in. End-to-end outsourcing of retail execution across CZ and SK.",
        }}
        path="/shelfmasters"
      />
      <Hero />
      <Benefits />
      <Split />
      <Ops />
      <Phases />
      <Models />
      <Kpis />
      <Reversibility />
      <Economics />
      <Cta />
    </div>
  );
}
